<!--
 ============================================================================
  Shell Bootstrap Entry
 ============================================================================
  Author        : Harvinder Singh
  Created On    : 2025-09-12
  Last Updated  : 2026-05
  File Purpose  :
  ---------------------------------------------------------------------------
  Main bootstrap entry for the Shell application.

  Responsibilities:
  - Authenticate the user
  - Resolve active account
  - Verify account cluster
  - Load packages & feature access
  - Initialize Split.io
  - Register dynamic routes
  - Load notifications & roles
  - Finalize shell readiness for MFEs

  IMPORTANT:
  ----------
  - Execution order is critical
  - Some steps intentionally fail-safe
  - Avoid reordering bootstrap phases
 ============================================================================
-->

<template>
  <v-app class="maropost-app">
    <!-- Splash screen while bootstrap is in progress -->
    <SplashScreen v-if="!isAccountReady" />

    <!-- Render resolved layout -->
    <component
      v-else
      :is="layouts[selectedLayout as keyof typeof layouts]"
    />

  </v-app>
</template>

<script setup lang="ts">
/* ============================================================================
 * IMPORTS
 * ============================================================================
 */
import { ref, onMounted } from 'vue'
import { useRouter, type Router } from 'vue-router'

/* Layouts */
import AppLayout from '@/lib/layouts/AppLayout.vue'
import DefaultLayout from '@/lib/layouts/DefaultLayout.vue'
import AuthLayout from '@/lib/layouts/AuthLayout.vue'

/* UI */
import { SplashScreen } from '@/lib/components'

/* Stores */
import { useUserStore } from '@/lib/stores'

/* Services */
import {
  $accountService,
  useMarketingService,
  useNotificationService,
} from '@/lib/services'

/* Config & Utils */
import { AppConfig } from '@/lib/config'
import { markShellReady } from './lib/config/spa/shell-ready'

import {
  verifyAccountCluster,
} from './lib/utils/utils'

import {
  fetchAccounts,
  fetchAccountIdFromUrl,
  findAccountFromAccounts,
  isProvisioningIncomplete,
} from './lib/utils/accounts'

import {
  getCombinedFeatures,
  checkFreetrailAccount,
} from './lib/utils/features'

import {
  getAccountSwitchPath,
  isActiveAccountPath,
} from './lib/utils/shell'

import { setPreviousProductPlans } from './lib/utils/previousProductPlans'

import { getAmplitudeService } from '@/lib/services/amplitudeTrackingService'

/* Auth */
import keycloak from './lib/plugins/keycloak-config'

/* Routing */
import getDynamicRoutes from './lib/router/updateRoutes'

/* ============================================================================
 * TYPES
 * ============================================================================
 */

type UserStore = ReturnType<typeof useUserStore>

/* ============================================================================
 * CONSTANTS
 * ============================================================================
 */

const layouts = {
  app: AppLayout,
  default: DefaultLayout,
  auth: AuthLayout,
}

const PACKAGE_TO_PRODUCT_MAP: Record<string, string> = {
  mmc: 'marketing',
  msc: 'service',
  mcc: 'commerce',
}

const GENERIC_ERROR_MESSAGE = 'Something went wrong.'

const NO_ACCOUNT_MESSAGE =
  'No linked account found for this user. Kindly contact Admin.'

/* ============================================================================
 * STATE
 * ============================================================================
 */

const selectedLayout = ref<'app' | 'default' | 'auth'>('app')
const isAccountReady = ref(false)
const router = useRouter()

/* ============================================================================
 * BOOTSTRAP ENTRY
 * ============================================================================
 */

onMounted(async () => {
  const userStore = useUserStore()

  // Disable sales navigation by default
  userStore.setSaleNavigation(false)

  /* --------------------------------------------------------------------------
   * STEP 1: AUTHENTICATION GUARD
   * --------------------------------------------------------------------------
   */
  if (!keycloak || !keycloak.authenticated) {
    selectedLayout.value = 'auth'
    markApplicationReady()
    return
  }

  /* --------------------------------------------------------------------------
   * STEP 2: FAST ACCOUNT VALIDATION
   * --------------------------------------------------------------------------
   * If current URL already belongs to active account,
   * bootstrap can skip some expensive operations.
   */
  let skipAccountBootstrap = false
  // Capture plans before package refresh so AppLayout can detect purchase changes
  setPreviousProductPlans(userStore.getAccountId, {
    mmc: userStore.getCurrentPackagePlan('mmc'),
    mcc: userStore.getCurrentPackagePlan('mcc'),
    msc: userStore.getCurrentPackagePlan('msc'),
  })
  const isActivePath = isActiveAccountPath(
      window.location.pathname,
      userStore.getAccountId.toString()
  )
  const fromCheckout =
    new URLSearchParams(window.location.search).get('state') === 'succeeded'
  if (
    userStore.getAccountId && isActivePath && !fromCheckout
  ) {
    skipAccountBootstrap = true

    verifyAccountClusterSafe(userStore.getDefaultAccount)

    registerDynamicRoutes(userStore, router)
  }

  /* --------------------------------------------------------------------------
   * STEP 3: LOAD USER PROFILE
   * --------------------------------------------------------------------------
   */
  try {
    await userStore.getUserData()
  } catch (err) {
    console.error('Failed to fetch user data:', err)
  }

  /* --------------------------------------------------------------------------
   * STEP 4: FETCH LINKED ACCOUNTS
   * --------------------------------------------------------------------------
   */
  let accounts: any[] = []

  try {
    accounts = await fetchAccounts()
  } catch (err) {
    redirectToIdentityDashboard(GENERIC_ERROR_MESSAGE)
    return
  }

  /* --------------------------------------------------------------------------
   * STEP 5: FETCH PRODUCT-ONLY ACCOUNTS
   * --------------------------------------------------------------------------
   * These products do not rely on core account packages.
   */
  await fetchStandaloneProductAccounts(userStore)

  /* --------------------------------------------------------------------------
   * STEP 6: HANDLE NO ACCOUNT FALLBACK
   * --------------------------------------------------------------------------
   */
  if (!accounts?.length) {
    redirectToOtherProduct(userStore)
    return
  }

  /* --------------------------------------------------------------------------
   * STEP 7: RESOLVE ACTIVE ACCOUNT
   * --------------------------------------------------------------------------
   */
  const account = await resolveActiveAccount(userStore, accounts)

  /* --------------------------------------------------------------------------
   * STEP 8: VERIFY ACCOUNT CLUSTER
   * --------------------------------------------------------------------------
   */
  if (!skipAccountBootstrap) {
    verifyAccountClusterSafe(account)
  }

  /* --------------------------------------------------------------------------
   * STEP 9: PERSIST ACCOUNT CONTEXT
   * --------------------------------------------------------------------------
   */
  persistAccountContext(userStore, account, accounts)

  /* --------------------------------------------------------------------------
   * STEP 10: FETCH PACKAGE FEATURES
   * --------------------------------------------------------------------------
   */
  try {
    const response = await $accountService.fetchPackageFeatures()

    const packageData = response.data.data

    userStore.setTrailPackageFeatures(packageData)

    userStore.setPackageFeatures(
      getCombinedFeatures(
        response.data,
        userStore.isCustomerPreferredUid
      )
    )

    const navigateToBilling = async () => {
      registerDynamicRoutes(userStore, router)

      await finalizeShellBootstrap()

      if(userStore.canPurchasePackage) 
        router.push(`/accounts/${userStore.getAccountId}/checkout`)
      else
        router.push(`/accounts/${userStore.getAccountId}/account-&-billing/subscriptions`)

    }

    checkFreetrailAccount(
      packageData,
      userStore,
      navigateToBilling
    )

    setUserAccessBasedOnPackage(packageData, userStore)

    await setAmplitudeUser(userStore, account)

    const hasMarketing = userStore.getProductAccess('marketing')
    const hasService = userStore.getProductAccess('service')
    const hasCommerce = userStore.getProductAccess('commerce')

    const hasCoreProducts =
      hasMarketing || hasService || hasCommerce

    const isExpiredTrialWithoutStandaloneProducts =
      ((userStore.getIsTrailAccount &&
      userStore.getIsTrialExpired) || userStore.getIsTrialExpired) &&
      !userStore.getProductAccess('neto') &&
      !userStore.getProductAccess('mhc')

    if (isExpiredTrialWithoutStandaloneProducts) {
      return
    }

    if (!hasCoreProducts) {
      // if (accounts.length > 1 && !userStore.getIsTrailAccount) {
      //   invalidAccount(account.id)
      //   return
      // }

      if (accounts.length) {
        registerDynamicRoutes(userStore, router)
      }

      redirectToOtherProduct(userStore)
      return
    }
  } catch (err) {
    invalidAccount(account.id)

    console.error('Failed to fetch package features:', err)

    return
  }

  /* --------------------------------------------------------------------------
   * STEP 11: FETCH ROLES & MARKETING INFO
   * --------------------------------------------------------------------------
   */
  try {
    const marketingService = useMarketingService()

    await Promise.all([
      $accountService.fetchRoles(),
      marketingService.fetchAccountInfo(),
    ])

    userStore.setRoles($accountService.roles.value)
  } catch (err) {
    console.error('Failed to fetch roles or marketing info:', err)

    userStore.setSingleProductAccess('marketing', false)

    // Prevent role-based access checks from failing
    userStore.setRoles({ roles: [] })

    if (!userStore.getProductAccess('commerce')) {
      invalidAccount(account.id)
      return
    }
  }

  /* --------------------------------------------------------------------------
   * STEP 12: FETCH NOTIFICATIONS
   * --------------------------------------------------------------------------
   */
  const canLoadNotifications =
    (
      userStore.getProductAccess('marketing') &&
      userStore.getProductAccessOnRoles('marketing')
    ) ||
    userStore.getProductAccess('commerce')

  if (canLoadNotifications) {
    try {
      useNotificationService.fetchNotifications()
    } catch (err) {
      console.error('Failed to fetch notifications:', err)
    }
  }

  /* --------------------------------------------------------------------------
   * STEP 13: REGISTER DYNAMIC ROUTES
   * --------------------------------------------------------------------------
   */
  if (!skipAccountBootstrap) {
    registerDynamicRoutes(userStore, router)
  }

  /* --------------------------------------------------------------------------
   * STEP 14: FINALIZE BOOTSTRAP
   * --------------------------------------------------------------------------
   */
  await finalizeShellBootstrap()
})

/* ============================================================================
 * HELPERS
 * ============================================================================
 */

/**
 * Marks application bootstrap as complete
 */
const markApplicationReady = () => {
  isAccountReady.value = true
}

/**
 * Redirects user to identity dashboard with message
 */
const redirectToIdentityDashboard = (message: string) => {
  const encodedMessage = encodeURIComponent(message)

  window.location.replace(
    `${AppConfig.IDENTITY_BASE_URL}/#/dashboard?code=400&message=${encodedMessage}`
  )
}

/**
 * Resolve active account from:
 * 1. URL accountId
 * 2. Existing store account
 * 3. Default account
 * 4. First available account
 */
const resolveActiveAccount = async (
  userStore: UserStore,
  accounts: any[]
) => {
  const parsedAccountId = Number.parseInt(fetchAccountIdFromUrl())

  const resolvedAccountId =
    Number.isFinite(parsedAccountId) && parsedAccountId > 0
      ? parsedAccountId
      : userStore.getAccountId ||
        userStore.getDefaultAccountId ||
        accounts[0].id

  return (
    (await findAccountFromAccounts(accounts, resolvedAccountId)) ||
    (await findAccountFromAccounts(
      accounts,
      userStore.getDefaultAccountId
    )) ||
    accounts[0]
  )
}

/**
 * Persist account-related state
 */
const persistAccountContext = (
  userStore: UserStore,
  account: any,
  accounts: any[]
) => {
  try {
    userStore.setAccountId(account.id)
    userStore.setDefaultAccount(account)
    userStore.setAccounts(accounts)
    userStore.setProductAccess(account)
  } catch (err) {
    console.error('Failed to persist account state:', err)
  }
}

/**
 * Derive MHC access from Keycloak token claims
 */
const setMhcAccessFromKeycloakToken = (userStore: UserStore) => {
  try {
    const tokenParsed = keycloak.tokenParsed as
      | Record<string, any>
      | undefined

    if (!tokenParsed) {
      userStore.setSingleProductAccess('mhc', false)
      userStore.setMhcStores([])
      return
    }

    const organizationIds = tokenParsed.organizationIds ?? []
    const roles: string[] = tokenParsed.realm_access?.roles ?? []

    const hasOrganizations =
      Array.isArray(organizationIds) &&
      organizationIds.length > 0

    const isMerchantAdmin = roles.includes(
      'adminapi:admin'
    )

    const hasMhcAccess = hasOrganizations || isMerchantAdmin

    userStore.setSingleProductAccess('mhc', hasMhcAccess)

    
    if (
      hasMhcAccess &&
      Array.isArray(organizationIds) &&
      organizationIds.length
    ) {
      userStore.setMhcStores(
        organizationIds.map((organizationId) => ({
          merchantID: organizationId
        }))
      )
    } else {
      userStore.setMhcStores([])
    }
  } catch (err) {
    userStore.setSingleProductAccess('mhc', false)
    userStore.setMhcStores([])

    console.error(
      'Failed to evaluate MHC access from Keycloak token:',
      err
    )
  }
}

/**
 * Fetch Neto standalone accounts and resolve MHC access from Keycloak
 */
const fetchStandaloneProductAccounts = async (
  userStore: UserStore
) => {
  const [netoResult] = await Promise.allSettled([
    $accountService.fetchNetoAccount({
      params: { filters: 'neto/tenancies' },
    }),
  ])

  /* Neto */
  if (netoResult.status === 'fulfilled') {
    const netoAccounts =
      $accountService.netoAccounts.value || []

    userStore.setSingleProductAccess(
      'neto',
      netoAccounts.length > 0
    )

    userStore.setNetoStores(netoAccounts)
  } else {
    userStore.setSingleProductAccess('neto', false)

    console.error(
      'Failed to fetch Neto accounts:',
      netoResult.reason
    )
  }

  /* MHC */
  setMhcAccessFromKeycloakToken(userStore)
}

/**
 * Disable product access when package missing
 */
const setUserAccessBasedOnPackage = (
  packages: Record<string, any>,
  userStore: UserStore
) => {
  if (!packages || typeof packages !== 'object') {
    return
  }

  Object.entries(PACKAGE_TO_PRODUCT_MAP).forEach(
    ([packageKey, productKey]) => {
      const pkg = packages[packageKey]
      if (!pkg || !pkg.package_name) {
        userStore.setSingleProductAccess(productKey, false)
      }
    }
  )
}

/**
 * Redirect user to product-specific dashboards
 */
const redirectToOtherProduct = (userStore: UserStore) => {
  if (userStore.getProductAccess('neto')) {
    setAmplitudeUser(userStore, {})

    router.push('/neto/dashboard')

    markApplicationReady()

    return
  }

  if (userStore.getProductAccess('mhc')) {
    router.push('/merchandising/dashboard')

    markApplicationReady()

    return
  }

  // check if the account provisioning is not completed
  const provisioning = isProvisioningIncomplete()
  if (provisioning) {
    router.push('/account-setup')

    markApplicationReady()
    
    return
  }

  redirectToIdentityDashboard(NO_ACCOUNT_MESSAGE)
}

/**
 * Handles invalid account redirect
 */
const invalidAccount = (id: string) => {
  markApplicationReady()

  selectedLayout.value = 'default'

  router.push({
    path: '/select-account',
    query: { accountId: id },
  })
}

/**
 * Safe wrapper around cluster verification
 */
const verifyAccountClusterSafe = (account: any) => {
  try {
    verifyAccountCluster(account)
  } catch (err) {
    console.error(
      'Account cluster verification failed:',
      err
    )
  }
}

/**
 * Register routes dynamically
 */
const registerDynamicRoutes = (
  userStore: UserStore,
  router: Router
) => {
  const dynamicRoutes = getDynamicRoutes(userStore, router)

  for (const route of dynamicRoutes) {
    if (route.name && !router.hasRoute(route.name)) {
      router.addRoute(route)
    }
  }

  if (
    userStore.getIsTrailAccount &&
    userStore.getIsTrialExpired
  ) {
    return
  }

  if (window.location.pathname === '/') {
    /**
     * If account is free trial account, redirect to onboarding if not completed, otherwise redirect to dashboard
     */
    const endpoint = userStore.getIsTrailAccount && !userStore.isOnboardingCompleted ? 'onboarding' : 'dashboard'
    router.replace(
      `/accounts/${userStore.getAccountId}/${endpoint}`
    )

    return
  }

  const currentPath = window.location.pathname

  const newPath = getAccountSwitchPath(
    currentPath,
    userStore.getAccountId.toString()
  )

  const currentFullPath =
    router.currentRoute.value.fullPath

  if (currentFullPath !== newPath) {
    router.replace({
      path: newPath,
      query: router.currentRoute.value.query
    })
  }
}

/**
 * Initialize amplitude user
 */
const setAmplitudeUser = async (
  userStore: UserStore,
  account?: any
) => {
  try {
    const amplitudeService =
      await getAmplitudeService()

    amplitudeService.setUser(userStore, account)

    if (!userStore.getIsLoggedIn) {
      amplitudeService.trackEvent('User Logged In')
    }

    userStore.setIsLoggedIn(true)
  } catch (err) {
    console.error(
      'Amplitude initialization failed:',
      err
    )
  }
}

/**
 * Finalize shell bootstrap
 */
const finalizeShellBootstrap = async () => {
  try {
    await markShellReady()

    markApplicationReady()

    void import(
      './lib/config/spa/single-spa-config'
    ).catch(err =>
      console.error(
        'Failed to load single-spa config:',
        err
      )
    )
  } catch (err) {
    console.error(
      'Shell ready finalization failed:',
      err
    )

    markApplicationReady()
  }
}

</script>

<style scoped>
.v-application.theme--light {
  color: rgba(0, 0, 0, 0.87);
}

.v-application.theme--dark {
  color: rgba(255, 255, 255, 0.87);
}
</style>